// nTop parametric client (partner .ntop notebooks with declared scalar inputs
// and optional PRE-BAKED frame sets — one STL per deflection value — feeding
// the Control Surfaces workbench's nTop mode, FXC-9009).
//
// Server: thread/thread-api/app/routes/ntop.py, all under the project-scoped
// prefix (same auth as the other geometry routes):
//   GET  /projects/{id}/ntop/status
//   POST /projects/{id}/ntop/bake
//   GET  /projects/{id}/ntop/bake/{job_id}
//   POST /projects/{id}/ntop/bake/{job_id}/cancel
//   POST /projects/{id}/ntop/qa
//   POST /projects/{id}/ntop/slice
//
// Frame STLs are geometry:// refs — display them through the EXISTING
// tessellateGeometry(projectId, { stepPath: <stl ref> }) (same sidecar cache,
// so scrubbing frames never re-tessellates). The bake job route returns the
// SAME status shape as the control-surface cut job (BakeCutJobStatus), with
// `result` on complete being the frames MANIFEST rather than cut outputs —
// controlSurfaceBake.pollCutJob drives it unchanged.

import { apiClient } from "./client";
import type { BakeCutJobStatus } from "./controlSurface";
import type { CsSectionRec } from "../features/highlift/geometry/csEngine";

// --- status ----------------------------------------------------------------------

/** Whether/how the nTopCL bake engine is wired up in this environment. */
export interface NtopEngineStatus {
  enabled: boolean;
  /** 'off' = not configured; 'local' = ntopcl on the API host; 'ssh' = remote runner. */
  mode: "off" | "local" | "ssh";
  /** Human detail line (where the binary lives / why it is off). */
  detail: string;
}

/** One declared notebook input (from the .ntop sidecar). */
export interface NtopInput {
  /** Stable input name (the notebook's own identifier). */
  name: string;
  /** Human label; fall back to `name` when absent. */
  label?: string;
  type: "scalar" | "text";
  units?: string | null;
  min?: number;
  max?: number;
  default?: number;
  step?: number;
}

/** Mesh-QA record for one baked frame STL (null when QA has not been run). */
export interface NtopQa {
  watertight: boolean;
  triangles: number;
  /** Connected-component count of the mesh. */
  components: number;
  /** Edge-length distribution in model units (refinement readout). */
  edge: { min: number; p05: number; median: number; p95: number; max: number };
  bbox: number[];
  span: number;
  mean_chord: number | null;
  /** p95 edge length over mean chord — the refinement figure of merit. */
  edge_to_chord_p95: number | null;
}

/** One pre-baked frame: the axis value + its STL on the API host. */
export interface NtopFrame {
  /** The frames-axis value this STL was baked at (e.g. aileron deflection). */
  value: number;
  /** geometry:// ref — feed straight to /geometry/tessellate as stepPath. */
  stl: string;
  size_mb: number;
  qa: NtopQa | null;
}

/** A notebook's baked frame set: one STL per value along ONE scalar axis. */
export interface NtopFrameSet {
  /** The swept input's name (matches an entry in the notebook's inputs). */
  axis: string;
  /** The non-axis scalar values the frames were baked with. */
  fixed: Record<string, number>;
  frames: NtopFrame[];
  baked_at: string | null;
  /** 'ntopcl' = baked by the engine here; 'external' = dropped-in pre-baked STLs. */
  source: "ntopcl" | "external";
}

/** One .ntop notebook the backend exposes (scanned from the nTop dir). */
export interface NtopNotebook {
  /** Stable id — the file stem. */
  key: string;
  label: string;
  /** Path on the API host (informational; the bake route takes the key). */
  path: string;
  size_mb: number;
  inputs: NtopInput[];
  /** The pre-baked frame set, or null when nothing has been baked yet. */
  frames: NtopFrameSet | null;
}

export interface NtopStatusResponse {
  engine: NtopEngineStatus;
  /** The scanned notebooks directory, or null when unconfigured. */
  dir: string | null;
  notebooks: NtopNotebook[];
}

/** List the project's nTop notebooks + engine status (empty list when unconfigured). */
export async function listNtopStatus(projectId: string): Promise<NtopStatusResponse> {
  return apiClient<NtopStatusResponse>(`/projects/${projectId}/ntop/status`);
}

// --- bake (frame generation via nTopCL) --------------------------------------------

export interface NtopBakeRequest {
  notebookKey: string;
  /** The scalar input to sweep. */
  axis: string;
  /** Axis values to bake — one frame STL each. */
  values: number[];
  /** Values for the remaining scalar inputs, held fixed across the sweep. */
  fixed: Record<string, number>;
}

/** POST /bake acknowledgement — a job HANDLE, not geometry (mirror of the cut
 *  route's start response). Poll getNtopBakeJob(job_id) for the baked manifest. */
export interface NtopBakeStartResponse {
  /** Poll handle, or null when the bake was skipped (engine off). */
  job_id: string | null;
  status: string;
  feature_enabled: boolean;
  note?: string | null;
}

/** The bake job status: the EXACT BakeCutJobStatus shape, with `result` on a
 *  'complete' status carrying the fresh frames manifest instead of cut outputs. */
export interface NtopBakeJobStatus extends Omit<BakeCutJobStatus, "result"> {
  result: NtopFrameSet | null;
}

/** Bake a frame sweep with nTopCL. Job-shaped: returns a handle immediately;
 *  the notebook runs server-side (~minutes) and getNtopBakeJob polls it to the
 *  frames manifest. feature_enabled=false means the engine is off here. */
export async function startNtopBake(
  projectId: string,
  req: NtopBakeRequest,
): Promise<NtopBakeStartResponse> {
  return apiClient<NtopBakeStartResponse>(`/projects/${projectId}/ntop/bake`, {
    method: "POST",
    body: {
      notebook_key: req.notebookKey,
      axis: req.axis,
      values: req.values,
      fixed: req.fixed,
    },
  });
}

/** Poll an nTop bake job; a 'complete' status carries the frames manifest. */
export async function getNtopBakeJob(
  projectId: string,
  jobId: string,
): Promise<NtopBakeJobStatus> {
  return apiClient<NtopBakeJobStatus>(`/projects/${projectId}/ntop/bake/${jobId}`);
}

/** Request cancellation of a running bake (checked between frames server-side). */
export async function cancelNtopBakeJob(
  projectId: string,
  jobId: string,
): Promise<{ job_id: string; status: string }> {
  return apiClient<{ job_id: string; status: string }>(
    `/projects/${projectId}/ntop/bake/${jobId}/cancel`,
    { method: "POST", body: {} },
  );
}

// --- QA + 2D slice ------------------------------------------------------------------

/** Run mesh QA on one frame STL (for frames whose manifest carries qa: null). */
export async function ntopQa(
  projectId: string,
  req: { stlPath: string },
): Promise<NtopQa> {
  return apiClient<NtopQa>(`/projects/${projectId}/ntop/qa`, {
    method: "POST",
    body: { stl_path: req.stlPath },
  });
}

/** One sliced station: the /geometry/sections record shape + how many extra
 *  closed loops the cutting plane found beyond the main element (a deflected
 *  control surface crossing the plane as its own body, a nacelle, ...). */
export interface NtopSliceSection extends CsSectionRec {
  extra_loops: number;
}

export interface NtopSliceRequest {
  /** Frame STL to slice (the geometry:// ref from the manifest). */
  stlPath: string;
  spanAxis: "y" | "z";
  /** Span-fraction stations, 0..1. */
  stations: number[];
}

export interface NtopSliceResponse {
  /** Keyed by eta formatted '%.4f' — the same convention /geometry/sections uses. */
  sections: Record<string, NtopSliceSection>;
  /** Millimetres per model unit of the section coords. */
  unit_mm: number;
}

/** Slice 2D airfoil sections from a frame STL (feeds the High-Lift 2D path). */
export async function ntopSlice(
  projectId: string,
  req: NtopSliceRequest,
): Promise<NtopSliceResponse> {
  return apiClient<NtopSliceResponse>(`/projects/${projectId}/ntop/slice`, {
    method: "POST",
    body: { stl_path: req.stlPath, span_axis: req.spanAxis, stations: req.stations },
  });
}
